import { listExpensesForBranch } from '../../expenses/infra/expensesRepo';
import type { PurchaseOrderRow, PurchaseReceptionRow, PurchaseReturnRow } from '../domain/purchaseTypes';
import { getWebPurchasesSnapshotCache } from './webPurchasesSnapshotCache';
// ─── Types ───────────────────────────────────────────────────────────────────

export type SupplierAccountEntry = {
  id: string;
  date: string;
  type: 'order' | 'reception' | 'return' | 'expense';
  description: string;
  amount: number;
  status: string;
};

export type SupplierAccountSummary = {
  supplierId: string;
  supplierName: string;
  totalOrdered: number;
  totalReceived: number;
  totalExpenses: number;
  totalPaid: number;
  /** Crédito operativo por devoluciones (resta deuda). */
  returnCredit: number;
  balance: number;
  entries: SupplierAccountEntry[];
};

const CREDIT_NOTE_DOC_TYPES = new Set([3, 8, 13, 53]);

function isSupplierCreditNoteExpense(invoiceDocType: number | null | undefined): boolean {
  return invoiceDocType != null && CREDIT_NOTE_DOC_TYPES.has(invoiceDocType);
}

function mapOrderRow(r: Record<string, unknown>): PurchaseOrderRow {
  return {
    id: String(r.id ?? ''),
    branchId: String(r.branch_id ?? ''),
    supplierId: r.supplier_id != null ? String(r.supplier_id) : null,
    supplierName: r.supplier_name != null ? String(r.supplier_name) : null,
    status: (r.status ?? 'draft') as PurchaseOrderRow['status'],
    origin: r.origin === 'direct' ? 'direct' : 'order',
    orderNumber: r.order_number != null ? String(r.order_number) : null,
    total: Number(r.total ?? 0),
    notes: r.notes != null ? String(r.notes) : null,
    expectedDelivery: r.expected_delivery != null ? String(r.expected_delivery) : null,
    supplierInvoiceRef: r.supplier_invoice_ref != null ? String(r.supplier_invoice_ref) : null,
    createdAt: String(r.created_at ?? ''),
    createdBy: r.created_by != null ? String(r.created_by) : null,
    updatedAt: String(r.updated_at ?? ''),
    updatedBy: r.updated_by != null ? String(r.updated_by) : null,
    receivedAt: r.received_at != null ? String(r.received_at) : null,
    receivedBy: r.received_by != null ? String(r.received_by) : null,
    voidedAt: r.voided_at != null ? String(r.voided_at) : null,
    voidedBy: r.voided_by != null ? String(r.voided_by) : null,
    voidReason: r.void_reason != null ? String(r.void_reason) : null,
    closedAt: r.closed_at != null ? String(r.closed_at) : null,
    closedBy: r.closed_by != null ? String(r.closed_by) : null,
    closeReason: r.close_reason != null ? String(r.close_reason) : null,
  };
}

function mapReceptionRow(r: Record<string, unknown>): PurchaseReceptionRow {
  return {
    id: String(r.id ?? ''),
    branchId: String(r.branch_id ?? ''),
    purchaseOrderId: r.purchase_order_id != null ? String(r.purchase_order_id) : null,
    supplierId: r.supplier_id != null ? String(r.supplier_id) : null,
    supplierName: r.supplier_name != null ? String(r.supplier_name) : null,
    status: r.status === 'voided' ? 'voided' : 'active',
    receivedAt: String(r.received_at ?? ''),
    receivedBy: r.received_by != null ? String(r.received_by) : null,
    notes: r.notes != null ? String(r.notes) : null,
    supplierInvoiceRef: r.supplier_invoice_ref != null ? String(r.supplier_invoice_ref) : null,
    voidedAt: r.voided_at != null ? String(r.voided_at) : null,
    voidedBy: r.voided_by != null ? String(r.voided_by) : null,
    voidReason: r.void_reason != null ? String(r.void_reason) : null,
    createdAt: String(r.created_at ?? ''),
  };
}

function mapReturnRow(r: Record<string, unknown>): PurchaseReturnRow {
  return {
    id: String(r.id ?? ''),
    branchId: String(r.branch_id ?? ''),
    receptionId: r.reception_id != null ? String(r.reception_id) : null,
    supplierId: r.supplier_id != null ? String(r.supplier_id) : null,
    supplierName: r.supplier_name != null ? String(r.supplier_name) : null,
    status: r.status === 'voided' ? 'voided' : 'active',
    returnedAt: String(r.returned_at ?? ''),
    returnedBy: r.returned_by != null ? String(r.returned_by) : null,
    reason: r.reason != null ? String(r.reason) : null,
    notes: r.notes != null ? String(r.notes) : null,
    voidedAt: r.voided_at != null ? String(r.voided_at) : null,
    voidedBy: r.voided_by != null ? String(r.voided_by) : null,
    voidReason: r.void_reason != null ? String(r.void_reason) : null,
    createdAt: String(r.created_at ?? ''),
  };
}

function sumReceptionAmount(receptionId: string, receptionItems: Record<string, unknown>[]): number {
  return receptionItems
    .filter((item) => String(item.reception_id ?? '') === receptionId)
    .reduce((sum, item) => sum + Number(item.quantity ?? 0) * Number(item.unit_cost ?? 0), 0);
}

function sumReturnAmount(returnId: string, returnItems: Record<string, unknown>[]): number {
  return returnItems
    .filter((item) => String(item.return_id ?? '') === returnId)
    .reduce((sum, item) => sum + Number(item.quantity ?? 0) * Number(item.unit_cost ?? 0), 0);
}

function buildSupplierAccountSummary(params: {
  supplierId: string;
  supplierName: string;
  orders: PurchaseOrderRow[];
  receptions: PurchaseReceptionRow[];
  returns: PurchaseReturnRow[];
  receptionAmounts: Map<string, number>;
  returnAmounts: Map<string, number>;
  expenses: Awaited<ReturnType<typeof listExpensesForBranch>>;
}): SupplierAccountSummary {
  const { supplierId, supplierName, orders, receptions, returns, receptionAmounts, returnAmounts, expenses } =
    params;

  // "Comprado" = mercadería real comprometida: excluye borradores (intención) y anuladas.
  const totalOrdered = orders
    .filter((o) => o.status !== 'draft' && o.status !== 'cancelled')
    .reduce((sum, o) => sum + o.total, 0);
  const totalReceived = receptions.reduce((sum, r) => sum + (receptionAmounts.get(r.id) ?? 0), 0);
  const totalExpenses = expenses
    .filter((e) => e.status === 'active')
    .reduce((sum, e) => sum + e.amount, 0);
  const totalPaid = expenses
    .filter((e) => e.status === 'active')
    .reduce((sum, e) => sum + e.amountPaid, 0);
  const returnCredit = returns.reduce((sum, r) => sum + (returnAmounts.get(r.id) ?? 0), 0);
  const ncCredit = expenses
    .filter((e) => e.status === 'active' && isSupplierCreditNoteExpense(e.invoiceDocType))
    .reduce((sum, e) => sum + e.amount, 0);
  const balance = Math.max(0, totalExpenses - totalPaid - returnCredit - ncCredit);

  const entries: SupplierAccountEntry[] = [
    ...orders.map((o) => ({
      id: o.id,
      date: o.createdAt.slice(0, 10),
      type: 'order' as const,
      description: `Orden de compra${o.supplierInvoiceRef ? ` · ${o.supplierInvoiceRef}` : ''}`,
      amount: o.total,
      status: o.status,
    })),
    ...receptions.map((r) => ({
      id: r.id,
      date: r.receivedAt.slice(0, 10),
      type: 'reception' as const,
      description: `Recepción${r.supplierInvoiceRef ? ` · ${r.supplierInvoiceRef}` : ''}`,
      amount: receptionAmounts.get(r.id) ?? 0,
      status: r.status,
    })),
    ...returns.map((r) => ({
      id: r.id,
      date: r.returnedAt.slice(0, 10),
      type: 'return' as const,
      description: `Devolución${r.reason ? ` · ${r.reason}` : ''}`,
      amount: returnAmounts.get(r.id) ?? 0,
      status: r.status,
    })),
    ...expenses.map((e) => ({
      id: e.id,
      date: e.expenseDate,
      type: 'expense' as const,
      description: e.description ?? 'Gasto',
      amount: e.amount,
      status: e.paymentStatus,
    })),
  ];

  entries.sort((a, b) => b.date.localeCompare(a.date));

  return {
    supplierId,
    supplierName,
    totalOrdered,
    totalReceived,
    totalExpenses,
    totalPaid,
    returnCredit: returnCredit + ncCredit,
    balance,
    entries,
  };
}

// ─── Repo ────────────────────────────────────────────────────────────────────

export async function getSupplierAccountSummary(
  branchId: string,
  supplierId: string,
  supplierName: string,
): Promise<SupplierAccountSummary> {
    const cached = getWebPurchasesSnapshotCache();
        const orders = (cached?.purchaseOrders ?? [])
          .filter(
            (o) => String(o.branch_id ?? '') === branchId && String(o.supplier_id ?? '') === supplierId,
          )
          .map(mapOrderRow)
          .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));

        const receptions = (cached?.receptions ?? [])
          .filter(
            (r) =>
              String(r.branch_id ?? '') === branchId &&
              String(r.supplier_id ?? '') === supplierId &&
              String(r.status ?? '') === 'active',
          )
          .map(mapReceptionRow)
          .sort((a, b) => (a.receivedAt < b.receivedAt ? 1 : -1));

        const returns = (cached?.returns ?? [])
          .filter(
            (r) =>
              String(r.branch_id ?? '') === branchId &&
              String(r.supplier_id ?? '') === supplierId &&
              String(r.status ?? '') === 'active',
          )
          .map(mapReturnRow)
          .sort((a, b) => (a.returnedAt < b.returnedAt ? 1 : -1));

        const receptionAmounts = new Map<string, number>();
        for (const r of receptions) {
          receptionAmounts.set(r.id, sumReceptionAmount(r.id, cached?.receptionItems ?? []));
        }

        const returnAmounts = new Map<string, number>();
        for (const r of returns) {
          returnAmounts.set(r.id, sumReturnAmount(r.id, cached?.returnItems ?? []));
        }

        const expenses = await listExpensesForBranch({ branchId, supplierId });

        return buildSupplierAccountSummary({
          supplierId,
          supplierName,
          orders,
          receptions,
          returns,
          receptionAmounts,
          returnAmounts,
          expenses,
        });
}

export async function listSuppliersWithPurchases(
  branchId: string,
): Promise<Array<{ supplierId: string; supplierName: string; orderCount: number }>> {
    const cached = getWebPurchasesSnapshotCache();
        const counts = new Map<string, { supplierName: string; orderCount: number }>();
        for (const raw of cached?.purchaseOrders ?? []) {
          if (String(raw.branch_id ?? '') !== branchId) continue;
          const supplierId = raw.supplier_id != null ? String(raw.supplier_id) : '';
          if (!supplierId) continue;
          if (String(raw.status ?? '') === 'cancelled') continue;
          const existing = counts.get(supplierId);
          const supplierName = raw.supplier_name != null ? String(raw.supplier_name) : 'Sin nombre';
          counts.set(supplierId, {
            supplierName: existing?.supplierName ?? supplierName,
            orderCount: (existing?.orderCount ?? 0) + 1,
          });
        }
        return [...counts.entries()]
          .map(([supplierId, { supplierName, orderCount }]) => ({ supplierId, supplierName, orderCount }))
          .sort((a, b) => b.orderCount - a.orderCount);
}
